import { UserRole } from '@provenance/shared';

/**
 * Where a person lands after signing in (T-140).
 *
 * Their role's home page — unless the page was first opened at a link while signed out (an
 * email's "Open in Provenir"), and the person signing in may see it. A URL left over from
 * an earlier session is never followed: that is how a Portal Admin signing in after a Chamber
 * Admin used to land on the admin's screen.
 */

/** The home page for each role. */
export function homeFor(role: string): string {
  return role === UserRole.PLATFORM_SUPER_ADMIN ? '/platform/integrators' : '/';
}

/** Whether a role may open a console path at all. The API enforces this too. */
export function mayOpen(role: string, path: string): boolean {
  const under = (prefix: string) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`);
  if (under('/profile')) return true;
  if (role === UserRole.PLATFORM_SUPER_ADMIN) return under('/platform');
  if (under('/platform')) return false;
  if (under('/configuration')) return role === UserRole.CHAMBER_ADMIN;
  return true;
}

/**
 * The link this page was opened at. Captured once, when the console script first runs —
 * which is a fresh page load, so it can only be a link someone followed or typed. Every way a
 * session ends inside the console reloads the page at `/`, so a previous user's screen never
 * reaches here.
 */
let openedAt: string | null = (() => {
  if (typeof window === 'undefined') return null;
  const { pathname, search } = window.location;
  const link = `${pathname}${search}`;
  return pathname === '/' || pathname === '/activate' || pathname === '/reset' ? null : link;
})();

/** Where to go now that `role` has signed in. Single use. */
export function landingFor(role: string): string {
  const link = openedAt;
  openedAt = null;
  return link && mayOpen(role, link) ? link : homeFor(role);
}

/**
 * Why the last session ended, shown once on the sign-in screen. Kept in sessionStorage so it
 * survives the reload that ends a session, and goes away with the tab.
 */
const REASON_KEY = 'pv.signedOutReason';
export type SignedOutReason = 'idle' | 'ended' | 'elsewhere';

export function rememberSignedOutReason(reason: SignedOutReason): void {
  try {
    sessionStorage.setItem(REASON_KEY, reason);
  } catch {
    /* storage blocked: the reason is a courtesy */
  }
}

export function takeSignedOutReason(): SignedOutReason | null {
  try {
    const reason = sessionStorage.getItem(REASON_KEY) as SignedOutReason | null;
    sessionStorage.removeItem(REASON_KEY);
    return reason;
  } catch {
    return null;
  }
}
